import { useEffect, useState } from 'react';
import { useCreateScimToken } from '@tinct/core-api';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@tinct/web-components';
import { TriangleAlertIcon } from 'lucide-react';
import {
  SecretField,
  ShownOnceWarning,
} from '../../../components/shown-once-secret';

export type GenerateScimTokenModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
};

/**
 * Generates a SCIM token and shows its value, once. The value lives in this dialog's state and
 * nowhere else: the mutation is reset when the dialog closes (and `useCreateScimToken` never writes
 * it into a cache), so it is gone as soon as the admin confirms having saved it. Until they do, the
 * dialog cannot be closed; nor while the token is being generated, since closing then would create a
 * token whose value nobody ever sees, holding one of the workspace's two slots until revoked.
 */
export const GenerateScimTokenModal = ({
  open,
  onOpenChange,
  organizationId,
}: GenerateScimTokenModalProps) => {
  const [token, setToken] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const createToken = useCreateScimToken({
    onSuccess: (created) => setToken(created.token ?? null),
  });
  const { reset } = createToken;

  // Unmounted without passing through `close` (the page went away): the mutation's copy goes too.
  useEffect(() => reset, [reset]);

  const isShowingToken = token !== null;
  const canClose = !createToken.isPending && (!isShowingToken || saved);

  const close = () => {
    setToken(null);
    setSaved(false);
    reset();
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
    } else if (canClose) {
      close();
    }
  };

  // 409 (5702) once two tokens exist: the message says to revoke one first.
  const refusal = createToken.error?.error ?? null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        // While the token shows, the gated button below is the one way out.
        showCloseButton={!isShowingToken}
        onEscapeKeyDown={(event) => !canClose && event.preventDefault()}
        onInteractOutside={(event) => !canClose && event.preventDefault()}
      >
        {isShowingToken ? (
          <>
            <DialogHeader>
              <DialogTitle>Your new SCIM token</DialogTitle>
              <DialogDescription>
                Paste it into your identity provider, next to the base URL, as
                the bearer token of the SCIM connection.
              </DialogDescription>
            </DialogHeader>

            <ShownOnceWarning title="This token is shown only once.">
              Copy it now: once this dialog is closed nobody can see it again,
              only revoke it and generate another.
            </ShownOnceWarning>

            <SecretField
              value={token}
              ariaLabel="SCIM token"
              toastMessage="Token copied"
            />

            <div className="flex items-center gap-2">
              <Checkbox
                id="scim-token-saved"
                checked={saved}
                onCheckedChange={(checked) => setSaved(checked === true)}
              />
              <Label htmlFor="scim-token-saved">
                I have saved the token in my identity provider
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" disabled={!saved} onClick={close}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Generate a SCIM token</DialogTitle>
              <DialogDescription>
                Your identity provider calls the base URL with this token. It
                is shown once, right after it is generated.
              </DialogDescription>
            </DialogHeader>

            {refusal && (
              <Alert variant="important">
                <TriangleAlertIcon />
                <AlertDescription>{refusal}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={createToken.isPending}
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => createToken.mutate({ organizationId })}
                isLoading={createToken.isPending}
              >
                Generate token
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
